import { useEffect, useState, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import CommonLayout from "../../../layouts/CommonLayout";
import { useNavigate, useParams } from "react-router-dom";
import { getAuthConfig } from "../../../services/apiUtils";
import { allAPiServicesCall } from "../../../services/apiServices";
import { artistImageUrl, albumImageUrl, songImageUrl } from "../../../utils/utils";
import {
    FaUser,
    FaCompactDisc,
    FaMusic,
    FaChevronDown,
    FaPlay,
    FaArrowLeft,
    FaEdit,
    FaPlus
} from "react-icons/fa";
import "./ArtistAlbumsAndsongs.css";

const ALBUMS_LIMIT = 20;
const SONGS_LIMIT = 20;

const ArtistAlbumsAndsongs = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [albumsPage, setAlbumsPage] = useState(1);
    const [songsPage, setSongsPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState({ albums: false, songs: false });
    const navigate = useNavigate();
    const { artistId } = useParams("artistId");

    const fetchData = useCallback(async (aPage = 1, sPage = 1, append = false) => {
        try {
            if (!append) setLoading(true);
            const payload = {
                artist_id: artistId,
                albums_page: aPage,
                albums_limit: ALBUMS_LIMIT,
                songs_page: sPage,
                songs_limit: SONGS_LIMIT,
            };
            const response = await allAPiServicesCall.getArtistSongAndAlbums(getAuthConfig(), payload);
            if (response?.status === 200 && response?.success === true) {
                if (append) {
                    setData(prev => ({
                        ...prev,
                        albums: aPage > albumsPage ? [...(prev?.albums || []), ...response.data[0].albums] : prev?.albums,
                        songs: sPage > songsPage ? [...(prev?.songs || []), ...response.data[0].songs] : prev?.songs,
                    }));
                } else {
                    setData(response.data[0]);
                }
                setPagination(response.pagination);
            } else {
                setData(null);
            }
        } catch (err) {
            setError("Failed to fetch data");
        } finally {
            setLoading(false);
            setLoadingMore({ albums: false, songs: false });
        }
    }, [artistId, albumsPage, songsPage]);

    useEffect(() => {
        fetchData();
    }, [artistId]);

    const loadMoreAlbums = async () => {
        const nextPage = albumsPage + 1;
        setLoadingMore(prev => ({ ...prev, albums: true }));
        setAlbumsPage(nextPage);

        const payload = {
            artist_id: artistId,
            albums_page: nextPage,
            albums_limit: ALBUMS_LIMIT,
            songs_page: songsPage,
            songs_limit: SONGS_LIMIT,
        };
        const response = await allAPiServicesCall.getArtistSongAndAlbums(getAuthConfig(), payload);
        if (response?.status === 200) {
            setData(prev => ({
                ...prev,
                albums: [...(prev?.albums || []), ...response.data[0].albums],
            }));
            setPagination(response.pagination);
        }
        setLoadingMore(prev => ({ ...prev, albums: false }));
    };

    const loadMoreSongs = async () => {
        const nextPage = songsPage + 1;
        setLoadingMore(prev => ({ ...prev, songs: true }));
        setSongsPage(nextPage);

        const payload = {
            artist_id: artistId,
            albums_page: albumsPage,
            albums_limit: ALBUMS_LIMIT,
            songs_page: nextPage,
            songs_limit: SONGS_LIMIT,
        };
        const response = await allAPiServicesCall.getArtistSongAndAlbums(getAuthConfig(), payload);
        if (response?.status === 200) {
            setData(prev => ({
                ...prev,
                songs: [...(prev?.songs || []), ...response.data[0].songs],
            }));
            setPagination(response.pagination);
        }
        setLoadingMore(prev => ({ ...prev, songs: false }));
    };

    if (error) {
        return (
            <CommonLayout>
                <div className="artist-details-error">
                    <p>{error}</p>
                </div>
            </CommonLayout>
        );
    }

    const hasMoreAlbums = pagination?.albums && albumsPage < pagination.albums.totalPages;
    const hasMoreSongs = pagination?.songs && songsPage < pagination.songs.totalPages;

    return (
        <CommonLayout>
            {loading ? (
                <div className="artist-details-loading">
                    <div className="artist-details-loading-spinner">
                        <Spinner animation="border" />
                    </div>
                    <p>Loading artist details...</p>
                </div>
            ) : (
                <div className="artist-details-page">
                    {/* Back Button */}
                    <button className="artist-details-back" onClick={() => navigate(-1)}>
                        <FaArrowLeft /> Back
                    </button>

                    {/* Artist Hero Section */}
                    <div className="artist-hero">
                        <div className="artist-hero-bg"></div>
                        <div className="artist-hero-content">
                            <div className="artist-hero-image-wrapper">
                                <img
                                    src={artistImageUrl(data)}
                                    alt={data?.artist_name}
                                    className="artist-hero-image"
                                />
                                <div className="artist-hero-image-badge">
                                    <FaUser />
                                </div>
                            </div>
                            <div className="artist-hero-info">
                                <span className="artist-hero-label">Artist</span>
                                <h1 className="artist-hero-name">{data?.artist_name}</h1>
                                <div className="artist-hero-stats">
                                    <div className="artist-hero-stat">
                                        <FaCompactDisc className="artist-hero-stat-icon" />
                                        <span className="artist-hero-stat-value">{pagination?.albums?.total || 0}</span>
                                        <span className="artist-hero-stat-label">Albums</span>
                                    </div>
                                    <div className="artist-hero-stat">
                                        <FaMusic className="artist-hero-stat-icon" />
                                        <span className="artist-hero-stat-value">{pagination?.songs?.total || 0}</span>
                                        <span className="artist-hero-stat-label">Songs</span>
                                    </div>
                                </div>
                                <button
                                    className="artist-hero-edit-btn"
                                    onClick={() => navigate(`/edit-artist/${artistId}`)}
                                >
                                    <FaEdit /> Edit Artist
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Albums Section */}
                    <div className="artist-section">
                        <div className="artist-section-header">
                            <div className="artist-section-title-wrapper">
                                <div className="artist-section-icon">
                                    <FaCompactDisc />
                                </div>
                                <div>
                                    <h2 className="artist-section-title">Albums</h2>
                                    <p className="artist-section-subtitle">
                                        {pagination?.albums?.total || 0} albums available
                                    </p>
                                </div>
                            </div>
                        </div>

                        {data?.albums?.length > 0 ? (
                            <>
                                <div className="artist-grid">
                                    {data.albums.map((album) => (
                                        <div
                                            key={album.album_id}
                                            className="artist-card artist-card-album"
                                            onClick={() => navigate(`/album/songs/${album.album_id}`)}
                                        >
                                            <div className="artist-card-image-wrapper">
                                                <img
                                                    src={albumImageUrl(album)}
                                                    alt={album.album_name}
                                                    className="artist-card-image"
                                                />
                                                <div className="artist-card-overlay">
                                                    <div className="artist-card-play">
                                                        <FaPlay />
                                                    </div>
                                                </div>
                                                <div className="artist-card-badge">
                                                    <FaCompactDisc /> Album
                                                </div>
                                            </div>
                                            <div className="artist-card-content">
                                                <h3 className="artist-card-name">{album.album_name}</h3>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {hasMoreAlbums && (
                                    <div className="artist-load-more">
                                        <button
                                            className="artist-load-more-btn"
                                            onClick={loadMoreAlbums}
                                            disabled={loadingMore.albums}
                                        >
                                            {loadingMore.albums ? (
                                                <>
                                                    <Spinner size="sm" animation="border" />
                                                    Loading...
                                                </>
                                            ) : (
                                                <>
                                                    <FaChevronDown />
                                                    Load More Albums ({data?.albums?.length || 0} of {pagination.albums.total})
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="artist-empty">
                                <FaCompactDisc className="artist-empty-icon" />
                                <p>No albums found for this artist</p>
                            </div>
                        )}
                    </div>

                    {/* Songs Section */}
                    <div className="artist-section">
                        <div className="artist-section-header">
                            <div className="artist-section-title-wrapper">
                                <div className="artist-section-icon artist-section-icon-songs">
                                    <FaMusic />
                                </div>
                                <div>
                                    <h2 className="artist-section-title">Songs</h2>
                                    <p className="artist-section-subtitle">
                                        {pagination?.songs?.total || 0} songs available
                                    </p>
                                </div>
                            </div>
                        </div>

                        {data?.songs?.length > 0 ? (
                            <>
                                <div className="artist-grid">
                                    {data.songs.map((song) => (
                                        <div
                                            key={song.song_id}
                                            className="artist-card artist-card-song"
                                            onClick={() => navigate(`/lyrics/${song.isrc_code}`)}
                                        >
                                            <div className="artist-card-image-wrapper">
                                                <img
                                                    src={songImageUrl(song)}
                                                    alt={song.song_name}
                                                    className="artist-card-image"
                                                />
                                                <div className="artist-card-overlay">
                                                    <div className="artist-card-play">
                                                        <FaPlay />
                                                    </div>
                                                </div>
                                                <div className="artist-card-badge artist-card-badge-song">
                                                    <FaMusic /> Song
                                                </div>
                                            </div>
                                            <div className="artist-card-content">
                                                <h3 className="artist-card-name">{song.song_name}</h3>
                                                {song.genre && (
                                                    <span className="artist-card-genre">{song.genre}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {hasMoreSongs && (
                                    <div className="artist-load-more">
                                        <button
                                            className="artist-load-more-btn artist-load-more-btn-songs"
                                            onClick={loadMoreSongs}
                                            disabled={loadingMore.songs}
                                        >
                                            {loadingMore.songs ? (
                                                <>
                                                    <Spinner size="sm" animation="border" />
                                                    Loading...
                                                </>
                                            ) : (
                                                <>
                                                    <FaChevronDown />
                                                    Load More Songs ({data?.songs?.length || 0} of {pagination.songs.total})
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="artist-empty">
                                <FaMusic className="artist-empty-icon" />
                                <p>No songs found for this artist</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </CommonLayout>
    );
};

export default ArtistAlbumsAndsongs;
